import React, { useState, useEffect, useMemo } from 'react'
import { useField, useFormikContext } from 'formik'
import { format } from 'date-fns'
import { CalendarIcon } from 'lucide-react'
import './styles.css'
import { cn } from '@/lib/utils'

import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import { useTranslation } from 'react-i18next'
import debounce from 'lodash/debounce'

/** Error message following shadcn FormMessage pattern */
const FieldError = ({ meta }) =>
  meta.touched && meta.error ? (
    <p className="text-sm text-destructive">{meta.error}</p>
  ) : null

/** Text / number / email / password input */
const InputField = ({ label, bsSize, debounceTime = 500, type, ...props }) => {
  const [field, meta] = useField(props)
  const [value, setValue] = useState(field.value)

  const debChange = useMemo(() => debounce(field.onChange, debounceTime), [field.onChange])

  useEffect(() => {
    setValue(field.value)
  }, [field.value])

  return (
    <div className="space-y-2">
      {label && <Label htmlFor={field.name}>{label}</Label>}
      <Input
        id={field.name}
        type={type}
        name={field.name}
        onBlur={field.onBlur}
        onChange={(e) => {
          e.persist?.()
          setValue(e.target.value)
          debChange(e)
        }}
        value={value}
        placeholder={props.placeholder}
        disabled={props.disabled || props.readOnly}
        className={cn(meta.touched && meta.error && 'border-destructive')}
      />
      <FieldError meta={meta} />
    </div>
  )
}

/** Textarea field */
const TextareaField = ({ label, bsSize, debounceTime = 500, ...props }) => {
  const [field, meta] = useField(props)
  const [value, setValue] = useState(field.value)

  const debChange = useMemo(() => debounce(field.onChange, debounceTime), [field.onChange])

  useEffect(() => {
    setValue(field.value)
  }, [field.value])

  return (
    <div className="space-y-2">
      {label && <Label htmlFor={field.name}>{label}</Label>}
      <Textarea
        id={field.name}
        name={field.name}
        onBlur={field.onBlur}
        onChange={(e) => {
          e.persist?.()
          setValue(e.target.value)
          debChange(e)
        }}
        value={value}
        rows={props.rows || 3}
        placeholder={props.placeholder}
        disabled={props.disabled || props.readOnly}
        className={cn('resize-none', meta.touched && meta.error && 'border-destructive')}
      />
      <FieldError meta={meta} />
    </div>
  )
}

/** DateTime picker — shadcn Calendar + Popover + time selects */
const DateTimeInput = ({ type, label, bsSize, onChange = null, ...props }) => {
  const { t } = useTranslation()
  const { setFieldValue } = useFormikContext()
  const [field, meta] = useField(props)
  const [open, setOpen] = useState(false)

  const selectedDate = field.value ? new Date(field.value) : null

  const handleDateSelect = (date) => {
    if (!date) return
    // Preserve existing time when changing date
    const current = selectedDate || new Date()
    date.setHours(current.getHours(), current.getMinutes())
    setFieldValue(field.name, date.toISOString())
    if (onChange) onChange(date)
  }

  const handleTimeChange = (type, value) => {
    const date = selectedDate ? new Date(selectedDate) : new Date()
    if (type === 'hours') date.setHours(parseInt(value))
    if (type === 'minutes') date.setMinutes(parseInt(value))
    setFieldValue(field.name, date.toISOString())
    if (onChange) onChange(date)
  }

  const isDisabled = props.disabled || props.readOnly

  return (
    <div className="space-y-2">
      {label && <Label htmlFor={field.name}>{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            disabled={isDisabled}
            className={cn(
              'w-full justify-start text-left font-normal h-10 bg-card',
              !selectedDate && 'text-muted-foreground',
              meta.error && meta.touched && 'border-destructive',
            )}
          >
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
            {selectedDate
              ? format(selectedDate, 'yyyy/MM/dd HH:mm')
              : <span>{props.placeholder || t('datepicker_pick_date', 'Pick a date')}</span>
            }
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleDateSelect}
            initialFocus
          />
          <div className="flex items-center gap-2 border-t px-3 py-2">
            <span className="text-sm text-muted-foreground">{t('datepicker_time', 'Time')}:</span>
            <Select
              value={selectedDate ? String(selectedDate.getHours()).padStart(2, '0') : '12'}
              onValueChange={(v) => handleTimeChange('hours', v)}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 24 }, (_, i) => (
                  <SelectItem key={i} value={String(i).padStart(2, '0')}>
                    {String(i).padStart(2, '0')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm font-medium">:</span>
            <Select
              value={selectedDate ? String(selectedDate.getMinutes()).padStart(2, '0') : '00'}
              onValueChange={(v) => handleTimeChange('minutes', v)}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 12 }, (_, i) => (
                  <SelectItem key={i * 5} value={String(i * 5).padStart(2, '0')}>
                    {String(i * 5).padStart(2, '0')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </PopoverContent>
      </Popover>
      <FieldError meta={meta} />
    </div>
  )
}

/** Switch / checkbox toggle — shadcn Switch pattern */
const CheckboxField = ({ label, readOnly, ...props }) => {
  const [field, meta] = useField(props)
  const { setFieldValue } = useFormikContext()

  return (
    <div className="space-y-1">
      <div className="flex flex-row items-center gap-3 py-2">
        <Switch
          id={field.name}
          checked={!!field.value}
          onCheckedChange={(checked) => setFieldValue(field.name, checked)}
          disabled={readOnly}
        />
        <Label htmlFor={field.name} className="text-sm font-normal text-muted-foreground cursor-pointer mb-0">
          {label}
        </Label>
      </div>
      <FieldError meta={meta} />
    </div>
  )
}

/** Select dropdown — shadcn Select */
const DropdownField = ({ label, type, options = [], labelField = 'label', valueField = 'value', readOnly, disabled, ...props }) => {
  const { t } = useTranslation()
  const [field, meta] = useField(props)
  const { setFieldValue } = useFormikContext()

  return (
    <div className="space-y-2">
      {label && <Label htmlFor={field.name}>{label}</Label>}
      <Select
        value={field.value ? String(field.value) : undefined}
        onValueChange={(val) => setFieldValue(field.name, val)}
        disabled={disabled || readOnly}
      >
        <SelectTrigger
          id={field.name}
          className={cn(meta.touched && meta.error && 'border-destructive')}
        >
          <SelectValue placeholder={t('dropdownfield_select_placeholder', 'Select...')} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option[valueField]} value={String(option[valueField])}>
              {option[labelField]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <FieldError meta={meta} />
    </div>
  )
}

const TYPES = {
  radio: CheckboxField,
  checkbox: CheckboxField,
  text: InputField,
  number: InputField,
  email: InputField,
  password: InputField,
  textarea: TextareaField,
  date: DateTimeInput,
  datetime: DateTimeInput,
  select: DropdownField,
}

export default (props) => {
  const Field = TYPES[props.type] || InputField
  return <Field {...props} />
}
